import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight, Building2, Users, Globe, DollarSign } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';

export default function ClientOnboarding() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    phone: '',
    industry: '',
    company_size: '',
    website: '',
    marketing_channels: [] as string[],
    marketing_budget: '',
    implementation_timeline: '',
    business_objectives: [] as string[],
    challenges: '',
    expectations: '',
    platforms: [] as string[]
  });

  const steps = [
    { id: 1, name: 'Company Info', icon: Building2 },
    { id: 2, name: 'Business Details', icon: Users },
    { id: 3, name: 'Marketing Preferences', icon: Globe },
    { id: 4, name: 'Social Platforms', icon: DollarSign }
  ];

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayToggle = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field as keyof typeof prev].includes(value)
        ? (prev[field as keyof typeof prev] as string[]).filter((item: string) => item !== value)
        : [...(prev[field as keyof typeof prev] as string[]), value]
    }));
  };

  const handleSubmit = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const { data: client, error: clientError } = await supabase
        .from('clients')
        .insert({
          user_id: user.id,
          company_name: formData.company_name,
          email: user.email,
          phone: formData.phone
        })
        .select()
        .single();

      if (clientError) throw clientError;

      await supabase.from('client_metadata').insert({
        client_id: client.id,
        industry: formData.industry,
        company_size: formData.company_size,
        website: formData.website,
        marketing_channels: formData.marketing_channels,
        marketing_budget: formData.marketing_budget,
        implementation_timeline: formData.implementation_timeline,
        business_objectives: formData.business_objectives,
        challenges: formData.challenges,
        expectations: formData.expectations
      });

      if (formData.platforms.length > 0) {
        await supabase.from('client_platforms').insert(
          formData.platforms.map(platform => ({
            client_id: client.id,
            platform: platform.toLowerCase(),
            is_active: true
          }))
        );
      }

      await supabase
        .from('profiles')
        .update({ role: 'client' })
        .eq('user_id', user.id);

      navigate('/client');
    } catch (error) {
      console.error('Error during onboarding:', error);
      alert('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const nextStep = () => setCurrentStep(prev => Math.min(prev + 1, steps.length));
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-8 py-6 bg-gradient-to-r from-blue-600 to-indigo-600">
            <h2 className="text-3xl font-bold text-white">Welcome to Edge Media Hub</h2>
            <p className="mt-2 text-blue-100">Let's get your account set up</p>
          </div>

          <div className="px-8 py-6">
            <nav aria-label="Progress">
              <ol className="flex items-center justify-between">
                {steps.map((step, stepIdx) => (
                  <li key={step.name} className={`relative ${stepIdx !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''} flex-1`}>
                    <div className="flex items-center">
                      <div className={`relative flex h-12 w-12 items-center justify-center rounded-full ${
                        currentStep > step.id ? 'bg-green-500' :
                        currentStep === step.id ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        {currentStep > step.id ? (
                          <Check className="h-6 w-6 text-white" />
                        ) : (
                          <step.icon className={`h-6 w-6 ${currentStep === step.id ? 'text-white' : 'text-gray-500'}`} />
                        )}
                      </div>
                      <span className="ml-4 text-sm font-medium text-gray-900 hidden sm:block">{step.name}</span>
                    </div>
                    {stepIdx !== steps.length - 1 && (
                      <div className="absolute top-6 left-12 right-0 h-0.5 bg-gray-200" />
                    )}
                  </li>
                ))}
              </ol>
            </nav>
          </div>

          <div className="px-8 py-8">
            {currentStep === 1 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Company Information</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.company_name}
                      onChange={(e) => handleInputChange('company_name', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Website</label>
                    <input
                      type="url"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Business Details</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Industry *</label>
                    <select
                      value={formData.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select an industry</option>
                      <option value="retail">Retail</option>
                      <option value="technology">Technology</option>
                      <option value="healthcare">Healthcare</option>
                      <option value="finance">Finance</option>
                      <option value="education">Education</option>
                      <option value="real-estate">Real Estate</option>
                      <option value="hospitality">Hospitality</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Company Size *</label>
                    <select
                      value={formData.company_size}
                      onChange={(e) => handleInputChange('company_size', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select company size</option>
                      <option value="1-10">1-10 employees</option>
                      <option value="11-50">11-50 employees</option>
                      <option value="51-200">51-200 employees</option>
                      <option value="201-500">201-500 employees</option>
                      <option value="500+">500+ employees</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Business Objectives</label>
                    <div className="grid grid-cols-2 gap-3">
                      {['Brand Awareness', 'Lead Generation', 'Customer Engagement', 'Sales Growth'].map(obj => (
                        <label key={obj} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.business_objectives.includes(obj)}
                            onChange={() => handleArrayToggle('business_objectives', obj)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{obj}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Marketing Preferences</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Current Marketing Channels</label>
                    <div className="grid grid-cols-2 gap-3">
                      {['Social Media', 'Email', 'SEO', 'PPC', 'Content Marketing', 'Events'].map(channel => (
                        <label key={channel} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={formData.marketing_channels.includes(channel)}
                            onChange={() => handleArrayToggle('marketing_channels', channel)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <span className="ml-2 text-sm text-gray-700">{channel}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Monthly Marketing Budget</label>
                    <select
                      value={formData.marketing_budget}
                      onChange={(e) => handleInputChange('marketing_budget', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select budget range</option>
                      <option value="<5000">Less than $5,000</option>
                      <option value="5000-10000">$5,000 - $10,000</option>
                      <option value="10000-25000">$10,000 - $25,000</option>
                      <option value="25000+">$25,000+</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Implementation Timeline</label>
                    <select
                      value={formData.implementation_timeline}
                      onChange={(e) => handleInputChange('implementation_timeline', e.target.value)}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="">Select timeline</option>
                      <option value="immediate">Immediate (1-2 weeks)</option>
                      <option value="1-month">Within 1 month</option>
                      <option value="1-3-months">1-3 months</option>
                      <option value="3+-months">3+ months</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-gray-900">Social Media Platforms</h3>
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">Select the platforms you want us to manage</p>
                  <div className="grid grid-cols-2 gap-4">
                    {['Instagram', 'Facebook', 'LinkedIn', 'Twitter', 'TikTok'].map(platform => (
                      <label key={platform} className="relative flex items-center p-4 border-2 rounded-lg cursor-pointer hover:border-blue-500 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.platforms.includes(platform)}
                          onChange={() => handleArrayToggle('platforms', platform)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-3 text-sm font-medium text-gray-900">{platform}</span>
                      </label>
                    ))}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Current Challenges (Optional)</label>
                    <textarea
                      value={formData.challenges}
                      onChange={(e) => handleInputChange('challenges', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="Tell us about any challenges you're facing with social media..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expectations from Us (Optional)</label>
                    <textarea
                      value={formData.expectations}
                      onChange={(e) => handleInputChange('expectations', e.target.value)}
                      rows={3}
                      className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      placeholder="What are your expectations from our service?"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="px-8 py-6 bg-gray-50 flex justify-between">
            {currentStep > 1 && (
              <button
                onClick={prevStep}
                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Previous
              </button>
            )}
            <div className="ml-auto">
              {currentStep < steps.length ? (
                <button
                  onClick={nextStep}
                  className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Next
                  <ArrowRight className="ml-2 h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={loading || !formData.company_name || !formData.phone}
                  className="inline-flex items-center px-6 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Complete Setup'}
                  <Check className="ml-2 h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
